import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, downloadPO, printPO, openUpload, openFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Receipt, Plus, Trash, CheckCircle, WarningCircle, FilePdf, Printer, UploadSimple, FileArrowDown, Truck, ImageSquare, ClockCounterClockwise, CaretDown, CaretRight, MagnifyingGlass } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");
const blankItem = { material_id: "", quantity: "", price: "", gst_rate: "18" };
const PAYMENT_TERMS = [0, 15, 30, 45, 60, 90];
const termDays = (t) => (t === null || t === undefined ? 30 : t);
const termLabel = (t) => (termDays(t) === 0 ? "Immediate" : `${termDays(t)} days`);

export default function PurchaseOrders() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("purchase-orders", "edit");
  const confirm = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [items, setItems] = useState([blankItem]);
  const [notes, setNotes] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [gstType, setGstType] = useState("intra");
  const [paymentTerms, setPaymentTerms] = useState("30");
  const [saving, setSaving] = useState(false);
  const [receivePo, setReceivePo] = useState(null);
  const [receiveQtys, setReceiveQtys] = useState([]);
  const [rejectQtys, setRejectQtys] = useState([]);
  const [rejectReason, setRejectReason] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [receiving, setReceiving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [payPo, setPayPo] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [paying, setPaying] = useState(false);
  const [termsPo, setTermsPo] = useState(null);
  const [newTerms, setNewTerms] = useState("30");

  const submitPayment = async () => {
    if (!payPo) return;
    const amt = Number(payAmount) || 0;
    if (amt <= 0) return toast.error("Enter a payment amount");
    setPaying(true);
    try {
      await api.post(`/purchase-orders/${payPo.id}/pay`, { amount: amt });
      toast.success(`₹${amt.toFixed(2)} payment recorded`);
      setPayPo(null); setPayAmount("");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPaying(false); }
  };

  const submitTerms = async () => {
    if (!termsPo) return;
    try {
      await api.patch(`/purchase-orders/${termsPo.id}/payment-terms`, { payment_terms: Number(newTerms) });
      toast.success(`Payment terms updated to ${termLabel(Number(newTerms))}`);
      setTermsPo(null);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const load = () => {
    api.get("/purchase-orders").then((r) => setPos(r.data)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Pre-fill from dashboard "Reorder" action
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const m = location.state?.reorder;
    if (m) {
      const need = m.min_stock - m.current_stock;
      setItems([{ material_id: m.id, quantity: String(need > 0 ? need : m.min_stock || 1), price: "", gst_rate: "18" }]);
      setOpen(true);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const mat = (id) => materials.find((m) => m.id === id);

  const openNew = () => { setSupplierId(""); setItems([blankItem]); setNotes(""); setExpectedDate(""); setGstType("intra"); setPaymentTerms("30"); setOpen(true); };

  const save = async () => {
    if (!supplierId) return toast.error("Select a supplier");
    const clean = items
      .filter((i) => i.material_id && Number(i.quantity) > 0)
      .map((i) => {
        const m = mat(i.material_id);
        return { material_id: i.material_id, material_name: m?.name || "", unit: m?.unit || "", quantity: Number(i.quantity), price: Number(i.price) || 0, gst_rate: Number(i.gst_rate) || 0 };
      });
    if (clean.length === 0) return toast.error("Add at least one material with a quantity");
    setSaving(true);
    try {
      await api.post("/purchase-orders", { supplier_id: supplierId, items: clean, notes, gst_type: gstType, expected_delivery_date: expectedDate || null, payment_terms: Number(paymentTerms) });
      toast.success("Purchase order created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openReceive = (po) => {
    setReceivePo(po);
    setReceiveQtys(po.items.map(() => ""));
    setRejectQtys(po.items.map(() => ""));
    setRejectReason("");
    setInvoiceFile(null);
    setReceiving(false);
  };

  const submitReceive = async () => {
    const rejects = rejectQtys.map((q) => Number(q) || 0);
    const totalRejected = rejects.reduce((s, q) => s + q, 0);
    if (totalRejected > 0 && !rejectReason.trim()) {
      toast.error("Please enter a rejection reason");
      return;
    }
    setReceiving(true);
    try {
      let invoice_file_id = null;
      if (invoiceFile) {
        const fd = new FormData();
        fd.append("file", invoiceFile);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        invoice_file_id = up.data.id;
      }
      const res = await api.post(`/purchase-orders/${receivePo.id}/receive`, {
        quantities: receiveQtys.map((q) => Number(q) || 0),
        rejected: rejects,
        rejection_reason: rejectReason.trim() || null,
        invoice_file_id,
      });
      const partial = res.data?.status === "partial";
      toast.success(`${receivePo.po_number} ${partial ? "partially received" : "received"} — stock updated`);
      setReceivePo(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setReceiving(false);
    }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Delete Purchase Order", description: "Delete this purchase order? This cannot be undone.", confirmText: "Delete" }))) return;
    try { await api.delete(`/purchase-orders/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const draftSubtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.price) || 0), 0);
  const draftTax = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.price) || 0) * (Number(i.gst_rate) || 0) / 100, 0);
  const draftGrand = draftSubtotal + draftTax;

  const openCount = pos.filter((p) => p.status === "pending" || p.status === "partial").length;
  const filterCount = (f) =>
    f === "all" ? pos.length
    : f === "open" ? openCount
    : pos.filter((p) => p.status === f).length;
  const visiblePos = pos.filter((p) =>
    statusFilter === "all" ? true
    : statusFilter === "open" ? (p.status === "pending" || p.status === "partial")
    : p.status === statusFilter
  ).filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (p.po_number || "").toLowerCase().includes(q)
      || (p.supplier_name || "").toLowerCase().includes(q)
      || (p.supplier_gst || "").toLowerCase().includes(q);
  });
  const FILTERS = [
    { key: "all", label: "All" },
    { key: "open", label: "Open Orders" },
    { key: "pending", label: "Pending" },
    { key: "partial", label: "Partial" },
    { key: "received", label: "Received" },
  ];

  return (
    <>
      <PageHeader title="Purchase Orders" subtitle="Reorder & Procurement">
        {canEdit && (<Button onClick={openNew} data-testid="add-po-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider transition-colors duration-100">
          <Plus size={16} weight="bold" className="mr-1" /> New Order
        </Button>)}
      </PageHeader>

      <div className="p-4 sm:p-8 space-y-4" data-testid="po-list">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2" data-testid="po-filter-bar">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                data-testid={`po-filter-${f.key}`}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors duration-100 ${statusFilter === f.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}
              >
                {f.label} <span className="font-mono opacity-70">({filterCount(f.key)})</span>
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-72">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PO number or company…"
              data-testid="po-search-input"
              className="rounded-none border-zinc-300 pl-9 focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>
        </div>
        {pos.length === 0 && (
          <div className="bg-white border border-zinc-300 p-12 text-center text-zinc-400">
            <Receipt size={32} weight="thin" className="mx-auto mb-3" /> No purchase orders yet.
          </div>
        )}
        {pos.length > 0 && visiblePos.length === 0 && (
          <div className="bg-white border border-zinc-300 p-12 text-center text-zinc-400" data-testid="po-filter-empty">
            {search.trim() ? `No purchase orders match "${search.trim()}".` : `No ${statusFilter === "open" ? "open" : statusFilter} purchase orders.`}
          </div>
        )}
        {visiblePos.map((po) => (
          <div key={po.id} className="bg-white border border-zinc-300" data-testid={`po-card-${po.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-zinc-300 bg-zinc-50">
              <button onClick={() => toggleExpand(po.id)} data-testid={`toggle-po-${po.id}`} className="flex items-center gap-3 text-left min-w-0 flex-1">
                {expanded[po.id] ? <CaretDown size={16} weight="bold" className="text-zinc-400 shrink-0" /> : <CaretRight size={16} weight="bold" className="text-zinc-400 shrink-0" />}
                <span className="font-mono font-bold text-sm bg-zinc-900 text-white px-2 py-1 shrink-0">{po.po_number}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">{po.supplier_name}</p>
                  <p className="text-[11px] text-zinc-500 font-mono">₹{po.total.toFixed(2)} · {po.items.length} item{po.items.length !== 1 ? "s" : ""}</p>
                </div>
              </button>
              <div className="flex items-center flex-wrap justify-end gap-1.5 sm:gap-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase text-white ${po.status === "received" ? "bg-[#16A34A]" : po.status === "partial" ? "bg-[#2563EB]" : "bg-[#EAB308]"}`}>
                  {po.status === "received" ? <CheckCircle size={12} weight="bold" /> : <WarningCircle size={12} weight="bold" />}
                  <span className="sm:hidden">{po.status === "partial" ? "Partial" : po.status}</span>
                  <span className="hidden sm:inline">{po.status === "partial" ? "Partially Received" : po.status}</span>
                </span>
                {po.status !== "received" && (
                  <Button size="sm" onClick={() => openReceive(po)} data-testid={`receive-po-${po.id}`} className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white text-xs uppercase font-bold">
                    <CheckCircle size={14} weight="bold" className="mr-1" /> {po.status === "partial" ? "Receive More" : "Receive"}
                  </Button>
                )}
                {po.invoice_file_id && (
                  <button onClick={() => openUpload(po.invoice_file_id).catch(() => toast.error("Could not open invoice"))} data-testid={`view-invoice-${po.id}`} title="View tax invoice" className="text-zinc-600 hover:text-primary p-1.5 sm:p-2 transition-colors duration-100"><FileArrowDown size={18} weight="bold" /></button>
                )}
                <button onClick={() => navigate(`/purchase-orders/${po.id}/timeline`)} data-testid={`po-timeline-${po.id}`} title="View timeline" className="text-zinc-600 hover:text-primary p-1.5 sm:p-2 transition-colors duration-100"><ClockCounterClockwise size={18} weight="bold" /></button>
                <button onClick={() => downloadPO(po.id, po.po_number).catch(() => toast.error("Download failed"))} data-testid={`download-po-${po.id}`} title="Download PDF" className="text-zinc-600 hover:text-primary p-1.5 sm:p-2 transition-colors duration-100"><FilePdf size={18} weight="bold" /></button>
                <button onClick={() => printPO(po.id).catch(() => toast.error("Print failed"))} data-testid={`print-po-${po.id}`} title="Print" className="text-zinc-600 hover:text-primary p-1.5 sm:p-2 transition-colors duration-100"><Printer size={18} weight="bold" /></button>
                <button onClick={() => remove(po.id)} className="text-[#DC2626] p-1.5 sm:p-2"><Trash size={16} weight="bold" /></button>
              </div>
            </div>
            {expanded[po.id] && (
            <div className="p-4 sm:p-6 overflow-x-auto">
              <table className="w-full text-sm min-w-[520px]">
                <thead>
                  <tr className="text-left text-xs uppercase text-zinc-500 border-b border-zinc-200">
                    <th className="py-2 font-bold">Material</th>
                    <th className="py-2 font-bold text-right">Qty</th>
                    {po.status !== "pending" && <th className="py-2 font-bold text-right">Received</th>}
                    <th className="py-2 font-bold text-right">Unit Price</th>
                    <th className="py-2 font-bold text-right">Taxable</th>
                    <th className="py-2 font-bold text-right">GST%</th>
                    <th className="py-2 font-bold text-right">Tax</th>
                    <th className="py-2 font-bold text-right">Line Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {po.items.map((it, i) => {
                    const recd = it.received_qty ?? 0;
                    const short = po.status !== "pending" && recd < it.quantity;
                    return (
                    <tr key={`${it.material_id || "row"}-${i}`}>
                      <td className="py-2 text-zinc-800">{it.material_name}</td>
                      <td className="py-2 text-right font-mono">{it.quantity} {it.unit}</td>
                      {po.status !== "pending" && (
                        <td className={`py-2 text-right font-mono ${short ? "text-[#EA580C] font-bold" : "text-[#16A34A]"}`}>
                          {recd} {it.unit}{short ? ` (−${(it.quantity - recd).toFixed(2)})` : ""}
                        </td>
                      )}
                      <td className="py-2 text-right font-mono">₹{it.price}</td>
                      <td className="py-2 text-right font-mono">₹{(it.taxable ?? it.quantity * it.price).toFixed(2)}</td>
                      <td className="py-2 text-right font-mono">{it.gst_rate ?? 0}%</td>
                      <td className="py-2 text-right font-mono">₹{(it.tax_amount ?? 0).toFixed(2)}</td>
                      <td className="py-2 text-right font-mono">₹{(it.line_total ?? it.quantity * it.price).toFixed(2)}</td>
                    </tr>
                  );})}
                </tbody>
                <tfoot className="text-sm">
                  <tr className="border-t border-zinc-300">
                    <td colSpan={po.status !== "pending" ? 7 : 6} className="py-1 text-right text-xs font-bold uppercase text-zinc-500">Taxable Value</td>
                    <td className="py-1 text-right font-mono">₹{(po.subtotal ?? po.total).toFixed(2)}</td>
                  </tr>
                  {po.gst_type === "inter" ? (
                    <tr>
                      <td colSpan={po.status !== "pending" ? 7 : 6} className="py-1 text-right text-xs font-bold uppercase text-zinc-500">IGST</td>
                      <td className="py-1 text-right font-mono">₹{(po.igst ?? 0).toFixed(2)}</td>
                    </tr>
                  ) : (
                    <>
                      <tr>
                        <td colSpan={po.status !== "pending" ? 7 : 6} className="py-1 text-right text-xs font-bold uppercase text-zinc-500">CGST</td>
                        <td className="py-1 text-right font-mono">₹{(po.cgst ?? 0).toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td colSpan={po.status !== "pending" ? 7 : 6} className="py-1 text-right text-xs font-bold uppercase text-zinc-500">SGST</td>
                        <td className="py-1 text-right font-mono">₹{(po.sgst ?? 0).toFixed(2)}</td>
                      </tr>
                    </>
                  )}
                  <tr className="border-t border-zinc-300">
                    <td colSpan={po.status !== "pending" ? 7 : 6} className="py-2 text-right text-xs font-bold uppercase text-zinc-900">Grand Total (incl. GST)</td>
                    <td className="py-2 text-right font-mono font-bold text-base">₹{po.total.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
              {po.status !== "pending" && (() => {
                const ordered = (po.items || []).reduce((s, it) => s + (it.quantity || 0), 0);
                const received = (po.items || []).reduce((s, it) => s + (it.received_qty || 0), 0);
                const rejected = (po.items || []).reduce((s, it) => s + (it.rejected_qty || 0), 0);
                if (ordered <= 0) return null;
                const recPct = (received / ordered) * 100;
                const rejPct = (rejected / ordered) * 100;
                return (
                  <div className="mt-4" data-testid={`po-fulfillment-bar-${po.id}`}>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="font-bold uppercase tracking-wider text-zinc-500">Fulfillment</span>
                      <span className="font-mono text-zinc-600">
                        <span className="text-[#16A34A]">{received}</span>
                        {rejected > 0 && <> · <span className="text-[#DC2626]">{rejected} rejected</span></>}
                        {" "}/ {ordered}
                      </span>
                    </div>
                    <div className="flex h-2.5 w-full overflow-hidden bg-zinc-200">
                      <div className="h-full bg-[#16A34A]" style={{ width: `${recPct}%`, transition: "width 300ms ease" }} title={`Received ${received}`} />
                      <div className="h-full bg-[#DC2626]" style={{ width: `${rejPct}%`, transition: "width 300ms ease" }} title={`Rejected ${rejected}`} />
                    </div>
                    {rejected > 0 && (
                      <div className="flex gap-4 mt-1.5 text-[11px] text-zinc-500">
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#16A34A] inline-block" /> Received</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#DC2626] inline-block" /> Rejected (awaiting replacement)</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-zinc-200 inline-block" /> Not yet delivered</span>
                      </div>
                    )}
                  </div>
                );
              })()}
              {po.expected_delivery_date && <p className="text-xs text-zinc-500 mt-3" data-testid={`po-expected-date-${po.id}`}>Expected delivery: <span className="font-mono">{po.expected_delivery_date}</span></p>}
              {(() => {
                const payable = (po.items || []).reduce((s, it) => s + (it.received_qty || 0) * (it.price || 0) * (1 + (it.gst_rate || 0) / 100), 0);
                if (payable <= 0) return null;
                const paid = po.amount_paid || 0;
                const pending = Math.round((payable - paid) * 100) / 100;
                const first = po.receipts?.[0]?.at;
                const due = first ? new Date(new Date(first).getTime() + termDays(po.payment_terms) * 86400000).toISOString().slice(0, 10) : null;
                const overdue = due && pending > 0 && due < new Date().toISOString().slice(0, 10);
                return (
                  <div className="mt-4 border-t border-zinc-200 pt-3" data-testid={`po-payment-${po.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Payment · {termLabel(po.payment_terms)} terms</p>
                      {canEdit && pending > 0 && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => { setTermsPo(po); setNewTerms(String(termDays(po.payment_terms))); }} data-testid={`change-terms-${po.id}`} className="rounded-none bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-100 text-[11px] uppercase font-bold h-7">Change Terms</Button>
                          <Button size="sm" onClick={() => { setPayPo({ ...po, _pending: pending }); setPayAmount(String(pending)); }} data-testid={`mark-paid-${po.id}`} className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white text-[11px] uppercase font-bold h-7">Mark Paid</Button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                      <div><p className="text-[10px] uppercase text-zinc-400">Payable</p><p className="font-mono">₹{payable.toFixed(2)}</p></div>
                      <div><p className="text-[10px] uppercase text-zinc-400">Paid</p><p className="font-mono text-[#16A34A]">₹{paid.toFixed(2)}</p></div>
                      <div><p className="text-[10px] uppercase text-zinc-400">Pending</p><p className={`font-mono font-bold ${pending > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>₹{pending.toFixed(2)}</p></div>
                    </div>
                    {due && <p className={`text-[11px] mt-1 ${overdue ? "text-[#DC2626] font-bold" : "text-zinc-400"}`}>{pending > 0 ? (overdue ? "Overdue since " : "Due by ") : "Fully paid · was due "}{due}</p>}
                    {(po.payments?.length ?? 0) > 0 && (
                      <div className="mt-2 space-y-0.5" data-testid={`po-payment-history-${po.id}`}>
                        <p className="text-[10px] uppercase tracking-wider text-zinc-400">Payment History</p>
                        {po.payments.map((pm, pi) => (
                          <p key={`${pm.at}-${pi}`} className="text-[11px] text-zinc-600 flex items-center gap-2">
                            <CheckCircle size={11} weight="fill" className="text-[#16A34A]" />
                            <span className="font-mono text-[#16A34A]">₹{(pm.amount || 0).toFixed(2)}</span>
                            <span className="text-zinc-400">·</span>{pm.date}
                            <span className="text-zinc-400">·</span>{pm.by_name}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              {po.notes && <p className="text-xs text-zinc-500 mt-3">Note: {po.notes}</p>}
              {(po.receipts?.length ?? 0) > 0 && (
                <div className="mt-4 border-t border-zinc-200 pt-3" data-testid={`po-delivery-history-${po.id}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Delivery History</p>
                  <div className="space-y-2">
                    {po.receipts.map((rcpt, ri) => {
                      const lines = (rcpt.quantities || [])
                        .map((q, qi) => ({ q, name: po.items[qi]?.material_name, unit: po.items[qi]?.unit }))
                        .filter((l) => l.q > 0);
                      const rejLines = (rcpt.rejected || [])
                        .map((q, qi) => ({ q, name: po.items[qi]?.material_name, unit: po.items[qi]?.unit }))
                        .filter((l) => l.q > 0);
                      return (
                        <div key={`rcpt-${ri}`} data-testid={`po-receipt-${po.id}-${ri}`} className="flex flex-wrap items-start justify-between gap-2 bg-zinc-50 border border-zinc-200 px-3 py-2">
                          <div className="flex items-center gap-2 text-xs">
                            <Truck size={14} weight="bold" className="text-[#16A34A]" />
                            <span className="font-mono text-zinc-700">Delivery {ri + 1}</span>
                            <span className="text-zinc-400">·</span>
                            <span className="text-zinc-600">{fmt(rcpt.at)}</span>
                            <span className="text-zinc-400">·</span>
                            <span className="text-zinc-500">by {rcpt.by_name}</span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {lines.map((l, li) => (
                              <span key={`rl-${li}`} className="font-mono text-[11px] bg-white border border-zinc-200 px-2 py-0.5 text-zinc-700">{l.name}: {l.q} {l.unit}</span>
                            ))}
                            {rejLines.map((l, li) => (
                              <span key={`rj-${li}`} className="font-mono text-[11px] bg-white border border-[#FCA5A5] px-2 py-0.5 text-[#DC2626]">✕ {l.name}: {l.q} {l.unit} rejected</span>
                            ))}
                            {rcpt.invoice_file_id && (
                              <button onClick={() => openUpload(rcpt.invoice_file_id).catch(() => toast.error("Could not open invoice"))} data-testid={`po-receipt-invoice-${po.id}-${ri}`} title={rcpt.invoice_filename || "Tax invoice"} className="text-primary hover:underline text-[11px] font-bold uppercase inline-flex items-center gap-1"><FileArrowDown size={12} weight="bold" /> Invoice</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {(po.rejections?.length ?? 0) > 0 && (
                <div className="mt-4 border-t border-[#FCA5A5] pt-3" data-testid={`po-rejection-history-${po.id}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#DC2626] mb-2">Rejection History</p>
                  <div className="space-y-1">
                    {po.rejections.map((rj, rji) => (
                      <p key={`rej-${rji}`} className="text-[11px] text-zinc-600 flex flex-wrap items-center gap-2" data-testid={`po-rejection-${po.id}-${rji}`}>
                        <span className="font-mono text-[#DC2626] font-bold">✕ {rj.qty} {rj.unit}</span>
                        <span className="text-zinc-700">{rj.material_name}</span>
                        <span className="text-zinc-400">·</span>
                        <span className="italic">{rj.reason}</span>
                        <span className="text-zinc-400">·</span>
                        <span className="text-zinc-500">{fmt(rj.at)} by {rj.by_name}</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-zinc-400 mt-2">Raised by {po.by_name} · {fmt(po.created_at)} {po.received_at ? `· Received ${fmt(po.received_at)}` : ""}</p>
            </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-2xl" data-testid="po-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">New Purchase Order</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 max-h-[55vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Supplier *</Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger data-testid="po-supplier-select" className="rounded-none border-zinc-300 focus:ring-2 focus:ring-primary">
                    <SelectValue placeholder={suppliers.length ? "Select supplier" : "No suppliers — add one first"} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.gst_number ? ` · ${s.gst_number}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">GST Type</Label>
                <Select value={gstType} onValueChange={setGstType}>
                  <SelectTrigger data-testid="po-gst-type-select" className="rounded-none border-zinc-300 focus:ring-2 focus:ring-primary"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="intra">Intra-State (CGST + SGST)</SelectItem>
                    <SelectItem value="inter">Inter-State (IGST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Payment Terms</Label>
                <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                  <SelectTrigger data-testid="po-payment-terms-select" className="rounded-none border-zinc-300 focus:ring-2 focus:ring-primary"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {PAYMENT_TERMS.map((d) => <SelectItem key={d} value={String(d)}>{termLabel(d)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Line Items</Label>
              {items.map((it, i) => (
                <div key={`poline-${it.material_id || "new"}-${i}`} className="flex items-center gap-2">
                  <Select value={it.material_id} onValueChange={(v) => { const n = [...items]; n[i].material_id = v; setItems(n); }}>
                    <SelectTrigger data-testid={`po-material-${i}`} className="rounded-none border-zinc-300 flex-1 focus:ring-2 focus:ring-primary"><SelectValue placeholder="Material" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" placeholder="Qty" data-testid={`po-qty-${i}`} value={it.quantity} onChange={(e) => { const n = [...items]; n[i].quantity = e.target.value; setItems(n); }} className="rounded-none border-zinc-300 font-mono w-20 focus-visible:ring-2 focus-visible:ring-primary" />
                  <Input type="number" placeholder="Price ₹" data-testid={`po-price-${i}`} value={it.price} onChange={(e) => { const n = [...items]; n[i].price = e.target.value; setItems(n); }} className="rounded-none border-zinc-300 font-mono w-24 focus-visible:ring-2 focus-visible:ring-primary" />
                  <Input type="number" placeholder="GST%" data-testid={`po-gst-${i}`} value={it.gst_rate} onChange={(e) => { const n = [...items]; n[i].gst_rate = e.target.value; setItems(n); }} className="rounded-none border-zinc-300 font-mono w-20 focus-visible:ring-2 focus-visible:ring-primary" />
                  <button onClick={() => setItems(items.filter((_, x) => x !== i))} className="text-[#DC2626] p-1"><Trash size={16} weight="bold" /></button>
                </div>
              ))}
              <Button variant="outline" onClick={() => setItems([...items, { ...blankItem }])} data-testid="po-add-line" className="rounded-none border-zinc-300 text-xs uppercase font-bold">
                <Plus size={14} weight="bold" className="mr-1" /> Add Line
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Expected Delivery Date <span className="text-zinc-400 normal-case font-normal">(optional)</span></Label>
              <Input type="date" data-testid="po-expected-date-input" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Notes</Label>
              <Textarea data-testid="po-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>

            <div className="border-t border-zinc-200 pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Taxable Value</span><span className="font-mono">₹{draftSubtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">{gstType === "inter" ? "IGST" : "CGST + SGST"}</span><span className="font-mono">₹{draftTax.toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-zinc-200 pt-1 font-bold"><span className="uppercase text-xs tracking-wide">Grand Total</span><span className="font-mono" data-testid="po-running-total">₹{draftGrand.toFixed(2)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="po-save-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{saving ? "Saving…" : "Create Order"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receivePo} onOpenChange={(v) => { if (!v) setReceivePo(null); }}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-lg" data-testid="receive-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Receive {receivePo?.po_number}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 max-h-[55vh] overflow-y-auto">
            <p className="text-sm text-zinc-500">Enter the <b>total delivered</b> quantity and how many were <b>rejected</b>. Good pieces (delivered − rejected) are added to stock; rejected pieces stay pending so the supplier can send a replacement.</p>
            <div className="space-y-2">
              {receivePo?.items.map((it, i) => {
                const already = it.received_qty || 0;
                const remaining = Math.max(it.quantity - already, 0);
                const delivered = Number(receiveQtys[i]) || 0;
                const rej = Number(rejectQtys[i]) || 0;
                const net = Math.max(delivered - rej, 0);
                return (
                <div key={i} className="border border-zinc-200 px-3 py-2">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[140px]">
                      <p className="text-sm font-medium text-zinc-900">{it.material_name}</p>
                      <p className="text-xs text-zinc-500">
                        Ordered <span className="font-mono">{it.quantity}</span>
                        {already > 0 && <> · Received <span className="font-mono text-[#16A34A]">{already}</span></>}
                        {" "}· Remaining <span className="font-mono text-[#EA580C]">{remaining}</span> {it.unit}
                      </p>
                    </div>
                    <div className="w-20">
                      <Label className="text-[10px] font-bold uppercase text-zinc-600">Delivered</Label>
                      <Input type="number" min="0" max={remaining} placeholder="0" data-testid={`receive-qty-${i}`} value={receiveQtys[i] ?? ""} onChange={(e) => { const n = [...receiveQtys]; n[i] = e.target.value; setReceiveQtys(n); }} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-primary" />
                    </div>
                    <div className="w-20">
                      <Label className="text-[10px] font-bold uppercase text-[#DC2626]">Rejected</Label>
                      <Input type="number" min="0" max={delivered} placeholder="0" data-testid={`reject-qty-${i}`} value={rejectQtys[i] ?? ""} onChange={(e) => { const n = [...rejectQtys]; n[i] = e.target.value; setRejectQtys(n); }} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-[#DC2626]" />
                    </div>
                  </div>
                  {delivered > 0 && (
                    <p className="text-[11px] text-zinc-500 mt-1" data-testid={`receive-net-${i}`}>
                      Good to stock: <span className="font-mono font-bold text-[#16A34A]">{net}</span> {it.unit}
                      {rej > 0 && <> · Rejected stays pending: <span className="font-mono font-bold text-[#DC2626]">{rej}</span></>}
                    </p>
                  )}
                </div>
              );})}
            </div>
            {rejectQtys.some((q) => Number(q) > 0) && (
              <div className="space-y-1" data-testid="reject-reason-block">
                <Label className="text-[10px] font-bold uppercase text-[#DC2626]">Rejection Reason <span className="text-[#DC2626]">*</span></Label>
                <Input placeholder="e.g. dented / wrong size / defective" data-testid="reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#DC2626]" />
                <p className="text-[11px] text-zinc-400">Rejected pieces stay pending on this PO for a replacement delivery.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Supplier Tax Invoice (image or PDF)</Label>
              <label className="flex items-center gap-3 border border-dashed border-zinc-300 px-4 py-4 cursor-pointer hover:border-primary transition-colors duration-100">
                <UploadSimple size={22} weight="bold" className="text-zinc-500" />
                <div className="flex-1">
                  <p className="text-sm text-zinc-700">{invoiceFile ? invoiceFile.name : "Click to upload the supplier's tax invoice"}</p>
                  <p className="text-[11px] text-zinc-400">JPG, PNG, WEBP or PDF · up to 10 MB</p>
                </div>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,application/pdf"
                  data-testid="invoice-file-input"
                  className="hidden"
                  onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceivePo(null)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={submitReceive} disabled={receiving} data-testid="receive-confirm-button" className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-bold uppercase tracking-wider">
              <CheckCircle size={16} weight="bold" className="mr-1" /> {receiving ? "Receiving…" : "Confirm & Add to Stock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record payment dialog */}
      <Dialog open={!!payPo} onOpenChange={(o) => { if (!o) setPayPo(null); }}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-md" data-testid="pay-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Record Payment</DialogTitle></DialogHeader>
          {payPo && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-500">{payPo.po_number} · {payPo.supplier_name} · outstanding <span className="font-mono text-[#DC2626] font-bold">₹{payPo._pending.toFixed(2)}</span></p>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-wider">Amount Paid (₹)</Label>
                <Input type="number" min="0" data-testid="pay-amount-input" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-[#16A34A]" />
                <p className="text-[11px] text-zinc-400">Enter a partial or full amount. Only accepted goods count toward the payable.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayPo(null)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={submitPayment} disabled={paying} data-testid="pay-confirm" className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-bold uppercase tracking-wider">{paying ? "Saving…" : "Record Payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change payment terms dialog */}
      <Dialog open={!!termsPo} onOpenChange={(o) => { if (!o) setTermsPo(null); }}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-sm" data-testid="terms-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Change Payment Terms</DialogTitle></DialogHeader>
          {termsPo && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-500">{termsPo.po_number} · {termsPo.supplier_name}</p>
              <Select value={newTerms} onValueChange={setNewTerms}>
                <SelectTrigger data-testid="new-terms-select" className="rounded-none border-zinc-300 focus:ring-2 focus:ring-primary"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {PAYMENT_TERMS.map((d) => <SelectItem key={d} value={String(d)}>{termLabel(d)}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-400">The due date recalculates from the first delivery date.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermsPo(null)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={submitTerms} data-testid="terms-confirm" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
